import React, { useState } from "react";

const Partnerships = () => {
  const [formType, setFormType] = useState("investor");
  const [formData, setFormData] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Send email notification
    console.log("Form submitted:", formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <>
      <style>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .partnership-header {
          animation: slideInUp 0.8s ease-out;
        }

        .partnership-card {
          animation: scaleIn 0.6s ease-out;
          transition: all 0.4s ease;
        }

        .partnership-card:nth-child(1) { animation-delay: 0.1s; }
        .partnership-card:nth-child(2) { animation-delay: 0.2s; }
        .partnership-card:nth-child(3) { animation-delay: 0.3s; }

        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-15px);
          }
        }

        .partnership-icon {
          animation: bounce 2s ease-in-out infinite;
        }

        .partnership-card:nth-child(1) .partnership-icon { animation-delay: 0s; }
        .partnership-card:nth-child(2) .partnership-icon { animation-delay: 0.2s; }
        .partnership-card:nth-child(3) .partnership-icon { animation-delay: 0.4s; }

        .partnership-card:hover {
          transform: translateY(-15px);
          box-shadow: 0 25px 50px rgba(13, 157, 173, 0.2);
        }

        .form-section {
          animation: slideInUp 0.8s ease-out 0.4s both;
        }

        .form-input {
          transition: all 0.3s ease;
        }

        .form-input:focus {
          box-shadow: 0 0 0 3px rgba(13, 157, 173, 0.1);
          transform: scale(1.02);
        }

        .form-tab {
          transition: all 0.3s ease;
        }

        .form-tab.active {
          background: linear-gradient(135deg, #1A7A7A, #0D9DAD);
          color: white;
        }

        .success-message {
          animation: slideInUp 0.5s ease-out;
        }
      `}</style>

      <section id="partnerships" className="py-24 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 partnership-header">
            <span className="inline-flex items-center gap-2 bg-slate-700/50 border border-slate-400/40 rounded-full px-4 py-2 text-xs font-semibold text-slate-300 mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 bg-slate-400 rounded-full animate-pulse" />
              Growth Opportunities
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
              Partnerships & Investment Opportunities
            </h2>
            <p className="text-white/80 text-lg leading-relaxed">
              Join Sri Lanka's leading healthcare revolution. Whether you're a medical professional or an investor, we have opportunities tailored for you.
            </p>
          </div>

          {/* Partnership Types */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: "💵",
                title: "For Investors",
                desc: "Be part of healthcare's digital transformation",
                benefits: ["Growth potential", "Market leadership", "ROI opportunities", "Impact investing"]
              },
              {
                icon: "👨‍⚕️",
                title: "For Doctors & Channel Center",
                desc: "Expand your patient reach and join our growing network",
                benefits: ["Access to patient network", "Digital tools", "Revenue sharing", "Marketing support"]
              },
              {
                icon: "🏪",
                title: "For Pharmacies & Labs",
                desc: "Integrate your services and grow your business",
                benefits: ["Automated order system", "Prescription integration", "Growth incentives", "Training support"]
              }
            ].map((item, idx) => (
              <div key={idx} className="partnership-card bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 hover:border-[#0D9DAD]/50">
                <div className="text-5xl mb-4 partnership-icon">{item.icon}</div>
                <h3 className="text-2xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-white/70 mb-6">{item.desc}</p>
                <ul className="space-y-2">
                  {item.benefits.map((benefit, i) => (
                    <li key={i} className="text-white/80 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#0D9DAD]"></span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Registration Forms */}
          <div className="form-section bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-12">
            {/* Tab Navigation */}
            <div className="flex gap-4 mb-12 border-b border-white/20 pb-4">
              {["investor", "partner"].map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    setFormType(type);
                    setFormData({});
                  }}
                  className={`form-tab px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                    formType === type
                      ? "bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
                      : "bg-white/10 text-white/70 hover:bg-white/20"
                  }`}
                >
                  {type === "partner" ? "🏥 Service Partner" : "💼 Investor"}
                </button>
              ))}
            </div>

            {/* Partner Form */}
            {formType === "partner" && (
              <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <input
                    type="text"
                    name="organizationName"
                    placeholder="Organization Name"
                    value={formData.organizationName || ""}
                    onChange={handleInputChange}
                    className="form-input bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-teal-400"
                    required
                  />
                  <input
                    type="text"
                    name="contactPerson"
                    placeholder="Contact Person"
                    value={formData.contactPerson || ""}
                    onChange={handleInputChange}
                    className="form-input bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-teal-400"
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email || ""}
                    onChange={handleInputChange}
                    className="form-input bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-teal-400"
                    required
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone || ""}
                    onChange={handleInputChange}
                    className="form-input bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-teal-400"
                    required
                  />
                  <select
                    name="serviceType"
                    value={formData.serviceType || ""}
                    onChange={handleInputChange}
                    className="form-input bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-teal-400 md:col-span-2"
                    required
                  >
                    <option value="" disabled>Select Service Type</option>
                    <option value="doctor" className="bg-slate-800">Doctor / Clinic</option>
                    <option value="pharmacy" className="bg-slate-800">Pharmacy</option>
                    <option value="laboratory" className="bg-slate-800">Laboratory</option>
                  </select>
                </div>
                <textarea
                  name="message"
                  placeholder="Tell us about your organization..."
                  value={formData.message || ""}
                  onChange={handleInputChange}
                  className="form-input w-full bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-teal-400 mb-6"
                  rows="4"
                  required
                ></textarea>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 rounded-lg font-bold hover:scale-105 transform transition-all duration-300"
                >
                  {submitted ? "✓ Application Sent!" : "Submit Partnership Application"}
                </button>
              </form>
            )}

            {/* Investor Form */}
            {formType === "investor" && (
              <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <input
                    type="text"
                    name="investorName"
                    placeholder="Full Name"
                    value={formData.investorName || ""}
                    onChange={handleInputChange}
                    className="form-input bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-teal-400"
                    required
                  />
                  <input
                    type="text"
                    name="company"
                    placeholder="Company/Organization"
                    value={formData.company || ""}
                    onChange={handleInputChange}
                    className="form-input bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-teal-400"
                    required
                  />
                  <input
                    type="email"
                    name="investorEmail"
                    placeholder="Email Address"
                    value={formData.investorEmail || ""}
                    onChange={handleInputChange}
                    className="form-input bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-teal-400"
                    required
                  />
                  <input
                    type="tel"
                    name="investorPhone"
                    placeholder="Phone Number"
                    value={formData.investorPhone || ""}
                    onChange={handleInputChange}
                    className="form-input bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-teal-400"
                    required
                  />
                  <input
                    type="text"
                    name="investmentType"
                    placeholder="Investment Type"
                    value={formData.investmentType || ""}
                    onChange={handleInputChange}
                    className="form-input bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-teal-400"
                    required
                  />
                  <input
                    type="text"
                    name="investmentAmount"
                    placeholder="Investment Amount Range"
                    value={formData.investmentAmount || ""}
                    onChange={handleInputChange}
                    className="form-input bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-teal-400"
                    required
                  />
                </div>
                <textarea
                  name="investorMessage"
                  placeholder="Tell us about your investment interests..."
                  value={formData.investorMessage || ""}
                  onChange={handleInputChange}
                  className="form-input w-full bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-teal-400 mb-6"
                  rows="4"
                  required
                ></textarea>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 rounded-lg font-bold hover:scale-105 transform transition-all duration-300"
                >
                  {submitted ? "✓ Inquiry Sent!" : "Submit Investment Inquiry"}
                </button>
              </form>
            )}

            {submitted && (
              <div className="success-message mt-6 max-w-2xl mx-auto bg-green-500/20 border border-green-400/50 rounded-lg p-4 text-green-300 text-center font-semibold">
                ✓ Thank you! We'll contact you soon.
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Partnerships;
